import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  CloudQueue as ProviderIcon,
  Memory as ModelIcon,
  Thermostat as TemperatureIcon,
  Notes as MaxTokensIcon,
  InfoOutlined as DescriptionIcon,
  Bolt as StreamingIcon,
  Chat as SystemMessageIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { seededConfigName, seededConfigDescription } from '../../utils/config/seededConfigText';
import { ChatConfigResponse } from '@/api/ApiClient';
import { ConfigDetailRow } from '../ui/ConfigDetailRow';

interface CurrentConfigTabProps {
  currentConfig: ChatConfigResponse | undefined;
}

export default function CurrentConfigTab({
  currentConfig,
}: CurrentConfigTabProps) {
  const { t } = useTranslation();

  if (!currentConfig) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {t('chatConfig.messages.noCurrentConfig')}
        </Typography>
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
        {/* Three-region flex row — mirrors ConfigCard in AllConfigsTab */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {/* LEFT: title + badges */}
          <Box sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, lineHeight: 1.3 }}>
              {seededConfigName(currentConfig.display_name, t) || t('chatConfig.tabs.currentConfiguration')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {currentConfig.is_default && (
                <Chip
                  label={t('chatConfig.currentConfig.default')}
                  color="primary"
                  size="small"
                  icon={<StarIcon />}
                />
              )}
              <Chip
                label={t('chatConfig.currentConfig.active')}
                color="success"
                size="small"
                icon={<CheckIcon />}
              />
            </Box>
          </Box>

          {/* Vertical divider — md+ only */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />

          {/* MIDDLE: parameter detail rows */}
          <Box sx={{ flexGrow: 1 }}>
            <Stack spacing={1.25}>
              <ConfigDetailRow
                icon={<ProviderIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.provider')}
                value={currentConfig.provider_type}
              />
              <ConfigDetailRow
                icon={<ModelIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.model')}
                value={currentConfig.model}
              />
              <ConfigDetailRow
                icon={<TemperatureIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.temperature')}
                value={currentConfig.temperature}
              />
              <ConfigDetailRow
                icon={<MaxTokensIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.maxTokens')}
                value={currentConfig.max_tokens}
              />
              <ConfigDetailRow
                icon={<StreamingIcon fontSize="small" />}
                label={t('chatConfig.currentConfig.streaming')}
                value={currentConfig.streaming ? t('common.yes') : t('common.no')}
              />
              {currentConfig.description && (
                <ConfigDetailRow
                  icon={<DescriptionIcon fontSize="small" />}
                  label={t('chatConfig.currentConfig.description')}
                  value={seededConfigDescription(currentConfig.description, t)}
                  alignItems="flex-start"
                />
              )}
            </Stack>
          </Box>
        </Box>

        {/* System message — full-width block below the flex row */}
        {currentConfig.system_message && (
          <Box sx={{ mt: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <SystemMessageIcon fontSize="small" />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                {t('chatConfig.currentConfig.systemMessage')}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                color: 'text.secondary',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {currentConfig.system_message}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
